import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const PrivacyPolicy = () => {
	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center justify-between">
						<Link to="/">
							<Logo showText={true} width={50} height={50} />
						</Link>
						<Link
							to="/"
							className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
						>
							Back to Home
						</Link>
					</div>
				</div>
			</header>

			{/* Content */}
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
					<h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
					<p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

					<div className="prose prose-lg max-w-none">
						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
							<p className="text-gray-700 mb-4">
								Welcome to iVault ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, which connects startups with investors for secure document sharing and collaboration.
							</p>
							<p className="text-gray-700">
								By using iVault, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
							
							<h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Personal Information</h3>
							<p className="text-gray-700 mb-4">
								We collect information that you provide directly to us, including:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Name, email address, and contact information</li>
								<li>Company name and business information</li>
								<li>User role (startup, investor, event organizer, etc.)</li>
								<li>Profile information and preferences</li>
								<li>Payment information (processed through secure third-party providers)</li>
							</ul>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Document and Content Information</h3>
							<p className="text-gray-700 mb-4">
								As a document sharing platform, we store and process:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Documents uploaded to the platform (financial reports, pitch decks, legal documents, etc.)</li>
								<li>Document metadata (file names, sizes, upload dates, etc.)</li>
								<li>Sharing permissions and access logs</li>
								<li>Comments and annotations on documents</li>
							</ul>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Usage Information</h3>
							<p className="text-gray-700 mb-4">
								We automatically collect information about how you use our platform:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Log data (IP address, browser type, access times, pages viewed)</li>
								<li>Device information (device type, operating system)</li>
								<li>Usage patterns and interactions with the platform</li>
								<li>Cookies and similar tracking technologies</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
							<p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>To provide, maintain, and improve our services</li>
								<li>To facilitate document sharing between startups and investors</li>
								<li>To manage user accounts and authenticate users</li>
								<li>To process transactions and send related information</li>
								<li>To send administrative information, updates, and security alerts</li>
								<li>To respond to your inquiries and provide customer support</li>
								<li>To detect, prevent, and address technical issues and security threats</li>
								<li>To comply with legal obligations and enforce our terms</li>
								<li>To analyze usage patterns and improve user experience</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
							<p className="text-gray-700 mb-4">
								We do not sell your personal information. We may share your information only in the following circumstances:
							</p>
							
							<h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 With Your Consent</h3>
							<p className="text-gray-700 mb-4">
								We share information when you explicitly consent, such as when you share documents with specific investors or startup networks.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Service Providers</h3>
							<p className="text-gray-700 mb-4">
								We may share information with third-party service providers who perform services on our behalf, such as cloud storage, payment processing, and analytics. These providers are contractually obligated to protect your information.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Legal Requirements</h3>
							<p className="text-gray-700 mb-4">
								We may disclose information if required by law, court order, or government regulation, or to protect our rights, property, or safety, or that of our users.
							</p>

							<h3 className="text-xl font-semibold text-gray-900 mb-3">4.4 Business Transfers</h3>
							<p className="text-gray-700 mb-4">
								In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
							<p className="text-gray-700 mb-4">
								We implement industry-standard security measures to protect your information:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Encryption of data in transit and at rest</li>
								<li>Secure authentication and access controls</li>
								<li>Regular security audits and vulnerability assessments</li>
								<li>Access logging and monitoring</li>
								<li>Secure data centers and infrastructure</li>
							</ul>
							<p className="text-gray-700">
								However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights and Choices</h2>
							<p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li><strong>Access:</strong> Request access to your personal information</li>
								<li><strong>Correction:</strong> Request correction of inaccurate information</li>
								<li><strong>Deletion:</strong> Request deletion of your information (subject to legal and contractual obligations)</li>
								<li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
								<li><strong>Opt-out:</strong> Opt out of certain communications and data processing</li>
								<li><strong>Account Settings:</strong> Update your account information and preferences through your account settings</li>
							</ul>
							<p className="text-gray-700">
								To exercise these rights, please contact us at privacy@ivault.com.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
							<p className="text-gray-700 mb-4">
								We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal or business purposes.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
							<p className="text-gray-700 mb-4">
								iVault is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
							<p className="text-gray-700 mb-4">
								Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using iVault, you consent to the transfer of your information to these countries.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
							<p className="text-gray-700 mb-4">
								We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
							<p className="text-gray-700 mb-4">
								If you have any questions about this Privacy Policy, please contact us:
							</p>
							<div className="bg-gray-50 p-4 rounded-lg">
								<p className="text-gray-700"><strong>Email:</strong> privacy@ivault.com</p>
								<p className="text-gray-700"><strong>Address:</strong> Techpuram, [Your Address]</p>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicy;

